/**
 * Global Scroll Lock Controller - elGordoFortnite
 * Ref-counted scroll locking for all modals, dialogs, and slide-over drawers.
 * Intercepts backdrop wheel and touch events with passive: false so that
 * the page underneath is 100% locked against scrolling on both desktop and mobile.
 */

const namedLocks = new Set<string>();
let anonymousLockCount = 0;
let scrollbarCompensation = 0;

export function getTotalLocks(): number {
  return namedLocks.size + anonymousLockCount;
}

export function lockScroll(key?: string): void {
  const prevTotal = getTotalLocks();
  if (key) {
    namedLocks.add(key);
  } else {
    anonymousLockCount++;
  }
  const currentTotal = getTotalLocks();

  if (prevTotal === 0 && currentTotal > 0) {
    if (typeof window !== 'undefined') {
      // Calculate any visible native scrollbar width to prevent layout shift on desktop
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        scrollbarCompensation = scrollbarWidth;
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    }
  }
}

export function unlockScroll(key?: string): void {
  const prevTotal = getTotalLocks();
  if (key) {
    namedLocks.delete(key);
  } else {
    anonymousLockCount = Math.max(0, anonymousLockCount - 1);
  }
  const currentTotal = getTotalLocks();

  if (prevTotal > 0 && currentTotal === 0) {
    if (typeof window !== 'undefined') {
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      document.body.style.overflow = '';
      if (scrollbarCompensation > 0) {
        document.body.style.paddingRight = '';
        scrollbarCompensation = 0;
      }
    }
  }
}

// Global active-interception of wheel & touch gestures when ANY modal is open
if (typeof window !== 'undefined') {
  const isAnyModalActive = () => {
    return (
      document.body?.classList.contains('modal-open') ||
      document.documentElement?.classList.contains('modal-open')
    );
  };

  // Prevent mouse wheel from scrolling background underneath open modals
  window.addEventListener(
    'wheel',
    (e: WheelEvent) => {
      if (!isAnyModalActive()) return;

      const target = e.target as HTMLElement | null;
      // Allow scrolling ONLY if target is inside an explicitly scrollable container
      const scrollable = target?.closest(
        '.overflow-y-auto, .overflow-auto, [data-scrollable="true"], .hd-drawer-body, .modal-scroll-area, textarea'
      ) as HTMLElement | null;

      if (!scrollable) {
        e.preventDefault();
        return;
      }

      // Check boundary conditions to prevent scroll chaining
      const { scrollTop, scrollHeight, clientHeight } = scrollable;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
        e.preventDefault();
      }
    },
    { passive: false }
  );

  // Prevent touch dragging from scrolling background underneath open modals on mobile
  window.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      if (!isAnyModalActive()) return;

      const target = e.target as HTMLElement | null;
      const scrollable = target?.closest(
        '.overflow-y-auto, .overflow-auto, [data-scrollable="true"], .hd-drawer-body, .modal-scroll-area, textarea'
      ) as HTMLElement | null;

      if (!scrollable) {
        e.preventDefault();
      }
    },
    { passive: false }
  );
}
